import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material';
import { useAssessmentMutations } from '../../../hooks/assessment-hooks';
import {
  Assessment,
  ASSESSMENT_PURPOSES,
  AssessmentPurpose,
} from '../../../support/assessments';
import InlineEditableText from './InlineEditableText';

/**
 * The assessment's own fields, edited in place at the top of the builder — no modal.
 *
 * Name, description, purpose, subject and the anonymous/published flags all commit their own
 * partial write as they change (`putItem` only touches the field sent), matching how the section
 * and question rows below edit inline. `key` is shown read-only: it was auto-assigned at creation
 * and is unique across all assessments, so it is not changed here.
 */

type Props = {
  assessment: Assessment;
};

const AssessmentMetaHeader: React.FC<Props> = ({ assessment }) => {
  const { update } = useAssessmentMutations('assessment');
  // Full-record PUT: the generic runtime treats a PUT as a full update and 400s if a required field
  // (key, name) is missing, so send the whole assessment with the patch applied.
  const commit = (patch: Record<string, any>) => update.mutate({ ...assessment, ...patch });

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Box flex={1} minWidth={0}>
          <InlineEditableText
            value={assessment.name}
            placeholder="Assessment name"
            variant="title"
            onCommit={(name) => commit({ name })}
          />
        </Box>
        <TextField
          select
          size="small"
          label="Purpose"
          value={assessment.purpose}
          onChange={(e) => commit({ purpose: e.target.value as AssessmentPurpose })}
          sx={{ minWidth: 190 }}
        >
          {ASSESSMENT_PURPOSES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box mt={1}>
        <InlineEditableText
          value={assessment.description}
          placeholder="Description (optional)"
          multiline
          onCommit={(description) => commit({ description })}
        />
      </Box>

      <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} mt={1.5}>
        <TextField
          size="small"
          label="Subject Type (optional)"
          defaultValue={assessment.subject_type ?? ''}
          helperText="What it's about, e.g. 'workshop'. Not a hard link."
          onBlur={(e) => {
            const next = e.target.value.trim() || null;
            if (next !== (assessment.subject_type ?? null)) commit({ subject_type: next });
          }}
          sx={{ minWidth: 240 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!assessment.allow_anonymous}
              onChange={(e) => commit({ allow_anonymous: e.target.checked })}
            />
          }
          label="Allow anonymous responses"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!assessment.is_published}
              onChange={(e) => commit({ is_published: e.target.checked })}
            />
          }
          label="Published (available to fill in)"
        />
      </Box>

      <Box mt={1}>
        <Chip size="small" variant="outlined" label={`key: ${assessment.key}`} />
      </Box>
    </Paper>
  );
};

export default AssessmentMetaHeader;
